import { useState, type FormEvent } from "react"
import { Head, usePage, router } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import type { SeoMeta } from "@/types"

const FEATURES = [
  {
    title: "Property prices",
    description:
      "Recent sold prices near the postcode and area price trends from the Land Registry.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
      </svg>
    ),
  },
  {
    title: "Crime data",
    description:
      "Monthly crime statistics from Police.uk, broken down by category for your neighbourhood.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.25-8.25-3.286Zm0 13.036h.008v.008H12v-.008Z" />
      </svg>
    ),
  },
  {
    title: "Flood risk",
    description:
      "Environment Agency monitoring stations and active flood warnings for the area.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5a17.92 17.92 0 0 1-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
      </svg>
    ),
  },
  {
    title: "Green Living Score",
    description:
      "A composite score covering green space, flood risk, conservation, walkability, cycling, and tree cover.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
      </svg>
    ),
  },
  {
    title: "Nearby amenities",
    description:
      "Schools, parks, restaurants, shops, and transport stops counted from OpenStreetMap data.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
      </svg>
    ),
  },
  {
    title: "Planning and health",
    description:
      "Conservation areas, listed buildings, green belt status, and local life expectancy data.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
      </svg>
    ),
  },
] as const

export default function PostcodeReportPage({ meta }: { meta: SeoMeta }) {
  const pageProps = usePage().props as Record<string, unknown>
  const flash = pageProps.flash as { error?: string } | undefined
  const flashError = flash?.error
  const [postcode, setPostcode] = useState("")
  const [loading, setLoading] = useState(false)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, "-")
    if (cleaned.length === 0) return
    setLoading(true)
    router.get(`/tools/postcode-report/${cleaned}`, {}, {
      onFinish: () => setLoading(false),
    })
  }

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      {/* Hero */}
      <section className="bg-cream px-6 py-24 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <div className="flex items-center justify-center gap-2">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Free neighbourhood report
            </p>
            <span className="rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red">
              Beta
            </span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
            What&rsquo;s your street really like
            <span className="text-red">?</span>
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg text-slate">
            Enter any UK postcode or outcode (e.g. BS8) for a free report covering
            property prices, crime, flood risk, amenities, and an environmental green score.
          </p>

          <form
            onSubmit={handleSubmit}
            className="mx-auto mt-10 flex max-w-md gap-3"
          >
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. BS8 1JA or BS8"
              className="flex-1 rounded-lg border border-stone bg-white px-4 py-3 text-charcoal placeholder:text-slate/50 focus:border-red/40 focus:outline-none focus:ring-2 focus:ring-red/10"
              aria-label="UK postcode"
            />
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-red px-6 py-3 font-semibold text-white transition-colors hover:bg-deep-red disabled:opacity-60"
            >
              {loading ? "Generating report..." : "Search"}
            </button>
          </form>

          {flashError && (
            <p className="mx-auto mt-4 max-w-md rounded-lg bg-red/5 px-4 py-3 text-sm text-red">
              {flashError}
            </p>
          )}
        </div>
      </section>

      {/* Feature cards */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="text-center font-display text-2xl font-bold text-charcoal">
              What&rsquo;s in the report
              <span className="text-red">?</span>
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-center text-sm text-slate">
              Six data sources combined into one free neighbourhood overview, powered by open government data.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="rounded-xl border border-stone bg-white p-6"
              >
                <div className="flex size-10 items-center justify-center rounded-lg bg-cream text-slate">
                  {feature.icon}
                </div>
                <h3 className="mt-4 font-display text-base font-bold text-charcoal">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {feature.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Know the neighbourhood before you list
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight helps estate agents turn listings into scroll-stopping
            video ads. Pair local data with compelling visuals.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="/features"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See features
            </a>
            <a
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Start free trial
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
